/**
 * UI Component Constants
 * Centralized configuration for reusable UI components
 */

// ============================================================================
// SPACING CONFIGURATIONS
// ============================================================================

/**
 * Vertical spacing classes for form and layout components
 */
export const SPACING = {
  xs: 'space-y-2',
  sm: 'space-y-3',
  md: 'space-y-6',
  lg: 'space-y-8',
  xl: 'space-y-10'
}

/**
 * Gap spacing for flex/grid layouts
 */
export const GAP_SPACING = {
  xs: 'gap-1',
  sm: 'gap-2',
  md: 'gap-3',
  lg: 'gap-4',
  xl: 'gap-6'
}

// ============================================================================
// SIZE CONFIGURATIONS
// ============================================================================

/**
 * Text size classes
 */
export const SIZES = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl'
}

/**
 * Icon sizes
 */
export const ICON_SIZES = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-8 h-8'
}

// ============================================================================
// LIST COMPONENT CONSTANTS
// ============================================================================

/**
 * List variant styling configurations
 */
export const LIST_VARIANTS = {
  default: '',
  bordered: 'border border-gray-200 dark:border-gray-700 rounded-lg',
  divided: 'divide-y divide-gray-200 dark:divide-gray-700',
  spaced: 'space-y-3',
  inline: 'flex flex-wrap gap-2'
}

/**
 * List item padding based on size
 */
export const LIST_ITEM_PADDING = {
  xs: 'py-1',
  sm: 'py-1.5',
  md: 'py-2',
  lg: 'py-3'
}

// ============================================================================
// FORM COMPONENT CONSTANTS
// ============================================================================

/**
 * Form action button positions
 */
export const FORM_ACTIONS_POSITIONS = {
  left: 'justify-start',
  right: 'justify-end',
  center: 'justify-center',
  between: 'justify-between'
}

/**
 * Form layout configurations
 */
export const FORM_LAYOUTS = {
  vertical: 'flex flex-col',
  horizontal: 'flex flex-row items-start',
  inline: 'flex flex-row items-center'
}

// ============================================================================
// COMMON UI COLORS
// ============================================================================

/**
 * Icon color classes
 */
export const UI_ICON_COLORS = {
  default: 'text-gray-500 dark:text-gray-400',
  primary: 'text-primary-500 dark:text-primary-400',
  secondary: 'text-gray-600 dark:text-gray-300',
  danger: 'text-danger-400',
  success: 'text-success-400',
  warning: 'text-warning-400',
  info: 'text-info-400',
  muted: 'text-gray-400 dark:text-gray-500'
}

// ============================================================================
// FORM FIELD ICON MAPPINGS
// ============================================================================

/**
 * Standard icon mappings for common form fields
 * Ensures consistency across all forms and views
 */
export const FORM_FIELD_ICONS = {
  // Environment & Infrastructure
  environment: { name: 'cog', color: UI_ICON_COLORS.info },
  
  // Date & Time
  eta: { name: 'calendar', color: UI_ICON_COLORS.primary },
  date: { name: 'calendar', color: UI_ICON_COLORS.primary },
  dueDate: { name: 'calendar', color: UI_ICON_COLORS.primary },
  
  // Links & References
  url: { name: 'link', color: UI_ICON_COLORS.info },
  urlToValidate: { name: 'link', color: UI_ICON_COLORS.info },
  prLinks: { name: 'github', color: UI_ICON_COLORS.success },
  ticketLink: { name: 'externalLink', color: UI_ICON_COLORS.info },
  
  // Features & Functionality
  features: { name: 'feature', color: UI_ICON_COLORS.info },
  feature: { name: 'feature', color: UI_ICON_COLORS.info },
  
  // Documentation & Notes
  description: { name: 'documentText', color: UI_ICON_COLORS.info },
  executionNotes: { name: 'clipboardList', color: UI_ICON_COLORS.warning },
  notes: { name: 'clipboardList', color: UI_ICON_COLORS.warning },
  
  // Activity & Communication
  activity: { name: 'clock', color: UI_ICON_COLORS.info },
  comments: { name: 'message', color: UI_ICON_COLORS.primary },
  comment: { name: 'message', color: UI_ICON_COLORS.primary }
}

/**
 * Text color classes
 */
export const UI_TEXT_COLORS = {
  default: 'text-gray-900 dark:text-white',
  muted: 'text-gray-500 dark:text-gray-400',
  primary: 'text-primary-600 dark:text-primary-400',
  danger: 'text-danger-600 dark:text-danger-400',
  success: 'text-success-600 dark:text-success-400',
  warning: 'text-warning-600 dark:text-warning-400'
}

/**
 * Border color classes
 */
export const UI_BORDER_COLORS = {
  default: 'border-gray-200 dark:border-gray-700',
  primary: 'border-primary-200 dark:border-primary-700',
  danger: 'border-danger-200 dark:border-danger-800',
  success: 'border-success-200 dark:border-success-800',
  warning: 'border-warning-200 dark:border-warning-800'
}

/**
 * Background color classes
 */
export const UI_BG_COLORS = {
  default: 'bg-white dark:bg-gray-800',
  muted: 'bg-gray-50 dark:bg-gray-700',
  primary: 'bg-primary-50 dark:bg-primary-900/10',
  danger: 'bg-danger-50 dark:bg-danger-900/20',
  success: 'bg-success-50 dark:bg-success-900/20',
  warning: 'bg-warning-50 dark:bg-warning-900/20'
}

// ============================================================================
// TABLE ACTION ICON COLORS
// ============================================================================

/**
 * Action icon colors for tables (used with HLButton color prop)
 * Consistent color scheme across all tables for action buttons
 */
export const ACTION_ICON_COLORS = {
  view: 'orange',      // View/Preview action - neutral orange
  edit: 'blue',        // Edit/Modify action - active blue
  clone: 'green',      // Clone/Copy action - positive green
  delete: 'red',       // Delete/Remove action - negative red
  restore: 'green'     // Restore action - positive green
}

// ============================================================================
// COMMON TRANSITIONS
// ============================================================================

/**
 * Transition duration classes
 */
export const TRANSITIONS = {
  fast: 'transition-all duration-150',
  normal: 'transition-all duration-200',
  slow: 'transition-all duration-300'
}

// ============================================================================
// COMMON SHADOWS
// ============================================================================

/**
 * Shadow classes
 */
export const SHADOWS = {
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  none: 'shadow-none'
}

// ============================================================================
// COMMON ROUNDED CORNERS
// ============================================================================

/**
 * Border radius classes
 */
export const ROUNDED = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  full: 'rounded-full'
}

// ============================================================================
// TOOLTIP / POPOVER VARIANTS
// ============================================================================

/**
 * Global tooltip variant used by every HighRise component family that supports
 * one (HLTooltip `variant`, HLEllipsis / HLMenu / HLDataTable `tooltipVariant`,
 * HLAvatarGroup `tooltipTheme`). Flip this one value to retheme tooltip surfaces
 * across the app.
 */
export type TooltipVariantType = 'light' | 'dark'

export const TOOLTIP_VARIANT: TooltipVariantType = 'light' 
// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get spacing class by size
 * @param {string} size - Size key (xs, sm, md, lg, xl)
 * @returns {string} Spacing class
 */
export function getSpacingClass(size = 'md') {
  return SPACING[size] || SPACING.md
}

/**
 * Get size class by size
 * @param {string} size - Size key (xs, sm, md, lg, xl)
 * @returns {string} Size class
 */
export function getSizeClass(size = 'md') {
  return SIZES[size] || SIZES.md
}

/**
 * Get list variant classes
 * @param {string} variant - Variant name
 * @returns {string} Variant classes
 */
export function getListVariant(variant = 'default') {
  return LIST_VARIANTS[variant] || LIST_VARIANTS.default
}

/**
 * Get form actions position classes
 * @param {string} position - Position name
 * @returns {string} Position classes
 */
export function getFormActionsPosition(position = 'right') {
  return FORM_ACTIONS_POSITIONS[position] || FORM_ACTIONS_POSITIONS.right
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  SPACING,
  GAP_SPACING,
  SIZES,
  ICON_SIZES,
  LIST_VARIANTS,
  LIST_ITEM_PADDING,
  FORM_ACTIONS_POSITIONS,
  FORM_LAYOUTS,
  UI_ICON_COLORS,
  UI_TEXT_COLORS,
  UI_BORDER_COLORS,
  UI_BG_COLORS,
  ACTION_ICON_COLORS,
  FORM_FIELD_ICONS,
  TRANSITIONS,
  SHADOWS,
  ROUNDED,
  getSpacingClass,
  getSizeClass,
  getListVariant,
  getFormActionsPosition
}

